import { Button, Field, Input, Select } from "@fluentui/react-components";
import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  CloudIcon,
  LockClosedIcon,
  Squares2X2Icon
} from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "../components/Brand";
import { Footer } from "../components/Footer";
import { useAuth } from "../hooks/useAuth";
import {
  type AzureProfile,
  parseAzureProfiles,
  serializeAzureProfilesCsv,
  serializeAzureProfilesJson
} from "../lib/profileFiles";
import { isMicrosoftLoginConfigured } from "../lib/microsoftAuth";
import type { AzureTenant } from "../lib/types";

const features = ["Multi-tenant", "Cost & Forecast", "Optimization", "Governance"];
const emptyProfile: AzureProfile = {
  name: "",
  tenantId: "",
  appId: "",
  clientSecret: ""
};

export function LoginPage() {
  const { connectMicrosoftTenant, discoverMicrosoft, login, session } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<AzureProfile>(emptyProfile);
  const [profiles, setProfiles] = useState<AzureProfile[]>([]);
  const [selectedProfileIndex, setSelectedProfileIndex] = useState<number | null>(null);
  const [profileMessage, setProfileMessage] = useState<{ kind: "error" | "success"; text: string } | null>(null);
  const [microsoftTenants, setMicrosoftTenants] = useState<AzureTenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [microsoftMessage, setMicrosoftMessage] = useState<string | null>(null);

  useEffect(() => {
    if (session.data?.authenticated && session.data.azureAccessReady) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, session.data]);

  async function connectTenant(tenantId: string) {
    setMicrosoftMessage(null);
    await connectMicrosoftTenant.mutateAsync(tenantId);
    navigate("/dashboard");
  }

  async function handleMicrosoftSignIn() {
    try {
      setMicrosoftMessage(null);
      const discovery = await discoverMicrosoft.mutateAsync();

      if (discovery.tenants.length === 0) {
        setMicrosoftMessage("The account does not have access to any Azure tenant.");
        return;
      }

      const preferredTenant = discovery.tenants.find((tenant) => tenant.tenantId === discovery.homeTenantId)
        || discovery.tenants[0];
      setMicrosoftTenants(discovery.tenants);
      setSelectedTenantId(preferredTenant.tenantId);

      if (discovery.tenants.length === 1) {
        await connectTenant(preferredTenant.tenantId);
      }
    } catch (error) {
      setMicrosoftMessage(error instanceof Error ? error.message : "Microsoft sign-in failed.");
    }
  }

  async function handleMicrosoftTenantSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    try {
      await connectTenant(selectedTenantId);
    } catch (error) {
      setMicrosoftMessage(error instanceof Error ? error.message : "Unable to connect to the selected tenant.");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await login.mutateAsync({
      tenantId: profile.tenantId,
      clientId: profile.appId,
      clientSecret: profile.clientSecret
    });
    navigate("/dashboard");
  }

  function updateProfile(patch: Partial<AzureProfile>) {
    setProfile((current) => ({ ...current, ...patch }));

    if (selectedProfileIndex !== null) {
      setProfiles((current) =>
        current.map((item, index) => index === selectedProfileIndex ? { ...item, ...patch } : item)
      );
    }
  }

  async function handleProfileFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    try {
      const importedProfiles = parseAzureProfiles(await file.text(), file.name);
      setProfiles(importedProfiles);
      setSelectedProfileIndex(0);
      setProfile(importedProfiles[0]);
      setProfileMessage({
        kind: "success",
        text: `${importedProfiles.length} profile${importedProfiles.length === 1 ? "" : "s"} loaded.`
      });
    } catch (error) {
      setProfileMessage({
        kind: "error",
        text: error instanceof Error ? error.message : "Unable to read the selected profile file."
      });
    }
  }

  function handleProfileSelection(event: ChangeEvent<HTMLSelectElement>) {
    const index = Number.parseInt(event.target.value, 10);
    const selected = profiles[index];

    if (selected) {
      setSelectedProfileIndex(index);
      setProfile(selected);
      setProfileMessage(null);
    }
  }

  function getProfilesForExport(): AzureProfile[] | null {
    if (!profile.tenantId.trim() || !profile.appId.trim() || !profile.clientSecret) {
      setProfileMessage({
        kind: "error",
        text: "Enter Tenant ID, App ID, and Client Secret before exporting."
      });
      return null;
    }

    const normalizedProfile = {
      ...profile,
      name: profile.name.trim() || "Azure profile",
      tenantId: profile.tenantId.trim(),
      appId: profile.appId.trim()
    };

    if (selectedProfileIndex === null) {
      return [normalizedProfile];
    }

    return profiles.map((item, index) => index === selectedProfileIndex ? normalizedProfile : item);
  }

  function downloadProfiles(format: "json" | "csv") {
    const exportProfiles = getProfilesForExport();

    if (!exportProfiles) {
      return;
    }

    const content = format === "json"
      ? serializeAzureProfilesJson(exportProfiles)
      : serializeAzureProfilesCsv(exportProfiles);
    const blob = new Blob([content], {
      type: format === "json" ? "application/json;charset=utf-8" : "text/csv;charset=utf-8"
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expense-analyzer-profiles-${new Date().toISOString().slice(0, 10)}.${format}`;
    link.click();
    URL.revokeObjectURL(url);
    setProfileMessage({
      kind: "success",
      text: `${exportProfiles.length} profile${exportProfiles.length === 1 ? "" : "s"} exported as ${format.toUpperCase()}.`
    });
  }

  return (
    <main className="login-page">
      <div className="login-visual" />
      <div className="login-overlay" />
      <section className="login-content">
        <motion.div className="login-copy" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <Brand />
          <div className="login-title-block">
            <span className="eyebrow">Azure Cost Management</span>
            <h1>Understand Your Azure Spending</h1>
            <p>Cost visibility, optimization and governance across your Azure estate</p>
          </div>
          <div className="feature-grid">
            {features.map((feature) => (
              <motion.div className="feature-item" key={feature} whileHover={{ x: 4 }}>
                <CheckCircleIcon />
                <span>{feature}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          className="login-card"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
        >
          <div className="login-card-header">
            <CloudIcon className="login-card-icon" />
            <div>
              <h2>Connect Azure</h2>
              <p>Expense Analyzer by Ciminaghi</p>
            </div>
          </div>

          <div className="microsoft-login-panel">
            <div className="microsoft-login-copy">
              <strong>Use your Microsoft account</strong>
              <span>Only tenants and subscriptions allowed by your Azure permissions will be visible.</span>
            </div>
            <Button
              appearance="primary"
              type="button"
              disabled={!isMicrosoftLoginConfigured() || discoverMicrosoft.isPending || connectMicrosoftTenant.isPending}
              icon={<Squares2X2Icon className="button-icon" />}
              className="full-button microsoft-login-button"
              onClick={handleMicrosoftSignIn}
            >
              {discoverMicrosoft.isPending ? "Signing in" : "Continue with Microsoft"}
            </Button>

            {microsoftTenants.length > 1 ? (
              <form className="tenant-picker" onSubmit={handleMicrosoftTenantSubmit}>
                <Field label="Azure tenant">
                  <Select value={selectedTenantId} onChange={(_, data) => setSelectedTenantId(data.value)}>
                    {microsoftTenants.map((tenant) => (
                      <option value={tenant.tenantId} key={tenant.tenantId}>
                        {tenant.displayName}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Button
                  appearance="primary"
                  type="submit"
                  disabled={!selectedTenantId || connectMicrosoftTenant.isPending}
                >
                  {connectMicrosoftTenant.isPending ? "Connecting" : "Open dashboard"}
                </Button>
              </form>
            ) : null}

            {!isMicrosoftLoginConfigured() ? (
              <div className="form-warning">Microsoft login requires VITE_ENTRA_CLIENT_ID at build time.</div>
            ) : null}
            {microsoftMessage ? <div className="form-error">{microsoftMessage}</div> : null}
          </div>

          {session.data?.servicePrincipalLoginEnabled ? (
            <>
              <div className="login-divider"><span>or</span></div>

              <details className="advanced-login">
                <summary>Connect with a Service Principal</summary>
                <form className="login-form" onSubmit={handleSubmit}>
              <div className="profile-file-panel">
                <div className="profile-file-heading">
                  <div>
                    <strong>Connection profiles</strong>
                    <span>Load or download Tenant ID, App ID, and Client Secret.</span>
                  </div>
                  <Button
                    appearance="outline"
                    type="button"
                    icon={<ArrowUpTrayIcon className="button-icon" />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Load
                  </Button>
                  <input
                    ref={fileInputRef}
                    className="profile-file-input"
                    type="file"
                    accept=".json,.csv,application/json,text/csv"
                    onChange={handleProfileFile}
                  />
                </div>

                {profiles.length > 0 ? (
                  <Field label="Loaded profile">
                    <Select value={String(selectedProfileIndex ?? 0)} onChange={handleProfileSelection}>
                      {profiles.map((item, index) => (
                        <option value={index} key={`${item.tenantId}-${item.appId}-${index}`}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}

                <div className="profile-file-actions">
                  <Button
                    appearance="subtle"
                    type="button"
                    icon={<ArrowDownTrayIcon className="button-icon" />}
                    onClick={() => downloadProfiles("json")}
                  >
                    Download JSON
                  </Button>
                  <Button
                    appearance="subtle"
                    type="button"
                    icon={<ArrowDownTrayIcon className="button-icon" />}
                    onClick={() => downloadProfiles("csv")}
                  >
                    Download CSV
                  </Button>
                </div>
                <p className="profile-security-note">
                  Exported files contain the Client Secret in plain text. Store them securely.
                </p>
              </div>

              <Field label="Profile name">
                <Input
                  value={profile.name}
                  onChange={(_, data) => updateProfile({ name: data.value })}
                  placeholder="Production, Customer A..."
                  autoComplete="off"
                />
              </Field>
              <Field label="Tenant ID" required>
                <Input
                  value={profile.tenantId}
                  onChange={(_, data) => updateProfile({ tenantId: data.value })}
                  autoComplete="off"
                />
              </Field>
              <Field label="App ID (Client ID)" required>
                <Input
                  value={profile.appId}
                  onChange={(_, data) => updateProfile({ appId: data.value })}
                  autoComplete="off"
                />
              </Field>
              <Field label="Client Secret" required>
                <Input
                  value={profile.clientSecret}
                  onChange={(_, data) => updateProfile({ clientSecret: data.value })}
                  type="password"
                  autoComplete="off"
                />
              </Field>
              {profileMessage ? (
                <div className={profileMessage.kind === "error" ? "form-error" : "form-success"}>
                  {profileMessage.text}
                </div>
              ) : null}
              {login.error ? <div className="form-error">{login.error.message}</div> : null}
              {session.data?.message ? <div className="form-warning">{session.data.message}</div> : null}
              <Button
                appearance="primary"
                type="submit"
                disabled={login.isPending}
                icon={<LockClosedIcon className="button-icon" />}
                className="full-button"
              >
                {login.isPending ? "Connecting" : "Connect securely"}
              </Button>
                </form>
              </details>
            </>
          ) : null}
        </motion.div>
      </section>
      <Footer />
    </main>
  );
}
